import { supabase } from './supabaseClient';

const bagApiKey = import.meta.env.VITE_BAG_API_KEY;

if (!bagApiKey) {
  console.error('Missing BAG API key (VITE_BAG_API_KEY)');
  throw new Error('Missing BAG API key (VITE_BAG_API_KEY)');
}

const BAG_API_BASE = 'https://api.bag.kadaster.nl/lvbag/individuelebevragingen/v2';

const SUPABASE_FUNCTION_BASE = '/functions/v1/bag-proxy';

/**
 * Helper function to parse a BAG address object into a consistent format.
 * @param {object} adres - The address object from the BAG API.
 * @returns {object|null} A formatted address object or null if invalid.
 */
function parseAdres(adres) {
  const id = adres.nummeraanduidingIdentificatie || adres.identificatie;
  if (!id) return null;

  let volledigAdres = adres.adres?.volledigAdres || adres.volledigAdres || '';

  if (!volledigAdres && adres.adresregel5 && adres.adresregel6) {
    const adresregel6Formatted = adres.adresregel6.replace(/\s{2,}/g, ' ').trim();
    volledigAdres = `${adres.adresregel5}, ${adresregel6Formatted}`;
  }

  if (!volledigAdres) {
    const openbareRuimteNaam = adres.openbareRuimteNaam || adres._embedded?.openbareRuimte?.openbareRuimte?.naam || '';
    const huisnummer = adres.huisnummer || '';
    const huisletter = adres.huisletter || '';
    const toevoeging = adres.huisnummertoevoeging || '';
    let postcode = adres.postcode || '';
    const woonplaats = adres.woonplaatsNaam || adres._embedded?.woonplaats?.woonplaats?.naam || '';

    if (postcode && postcode.length === 6 && !postcode.includes(' ')) {
      postcode = `${postcode.slice(0, 4)} ${postcode.slice(4)}`;
    }

    const huisnummerDeel = `${huisnummer}${huisletter}${toevoeging ? ` ${toevoeging}` : ''}`.trim();
    const straatHuisnummer = `${openbareRuimteNaam} ${huisnummerDeel}`.trim();
    const postcodeWoonplaats = `${postcode} ${woonplaats}`.trim();

    volledigAdres = [straatHuisnummer, postcodeWoonplaats].filter(Boolean).join(', ');
  }
  
  if (!volledigAdres) {
    volledigAdres = `Adres met ID ${id}`;
  }

  return {
    id: String(id),
    label: volledigAdres,
    rawData: { ...adres, type: 'Adres' },
  };
}

/**
 * Helper function to parse an OpenbareRuimte object from an address object.
 * @param {object} adres - The address object from the BAG API.
 * @returns {object|null} A formatted openbare ruimte object or null if invalid.
 */
function parseOpenbareRuimte(adres) {
  const orId = adres.openbareRuimteIdentificatie;
  if (!orId) return null;

  const orNaam = adres.openbareRuimteNaam || adres._embedded?.openbareRuimte?.openbareRuimte?.naam;
  const wplNaam = adres.woonplaatsNaam || adres._embedded?.woonplaats?.woonplaats?.naam;

  if (orNaam && wplNaam) {
    return {
      id: orId,
      label: `${orNaam}, ${wplNaam} (Hele straat)`,
      rawData: {
        type: 'OpenbareRuimte',
        identificatie: orId,
        openbareRuimteNaam: orNaam,
        woonplaatsNaam: wplNaam,
      },
    };
  }
  return null;
}

async function invokeBagProxy(endpoint, params = {}) {
  const { data, error } = await supabase.functions.invoke('bag-proxy', {
      body: { endpoint, params }
  });

  if (error) {
      console.error(`Error invoking Supabase function for endpoint ${endpoint}:`, error);
      throw error;
  }

  return data;
}

/**
 * Fetch BAG object by endpoint and id
 * @param {string} endpoint - e.g. 'adressen'
 * @param {string} id - e.g. adres id
 * @returns {Promise<any>}
 */
export async function fetchBagObject(endpoint, id) {
  const path = `${endpoint}/${id}`;
  const params = { expand: 'openbareRuimte,woonplaats' };

  const { data, error } = await supabase.functions.invoke('bag-proxy', {
    body: { path, params }
  });

  if (error) {
    throw new Error(`BAG API error: ${error.message}`);
  }
  return data;
}

/**
 * Zoek adressen via de BAG API met vrije tekst (straat, postcode, huisnummer, etc.)
 * @param {string} query - zoekterm (bijv. '2104VN 35')
 * @returns {Promise<Array<{ id: string, label: string }>>}
 */
export async function searchBagAdressen(query) {
  if (!query || query.length < 4) return [];

  try {
    const path = 'adressen';
    const params = {
      q: query,
      expand: 'openbareRuimte,woonplaats',
      pageSize: '20'
    };
    
    const { data, error } = await supabase.functions.invoke('bag-proxy', {
      body: { path, params }
    });
    
    if (error) {
        console.error(`Fout bij het zoeken naar adressen:`, error.message);
        return [];
    }
    
    if (!data?._embedded?.adressen) {
      return [];
    }

    const allAdressen = data._embedded.adressen;

    // Haal alle adresresultaten op
    const adresResults = allAdressen.map(parseAdres).filter(Boolean);

    // Haal unieke openbare ruimtes op
    const openbareRuimteResults = {};
    for (const adres of allAdressen) {
      const orId = adres.openbareRuimteIdentificatie;
      if (orId && !openbareRuimteResults[orId]) {
        const or = parseOpenbareRuimte(adres);
        if (or) {
          openbareRuimteResults[orId] = or;
        }
      }
    }

    const uniekeStraten = Object.values(openbareRuimteResults);

    // Als de query geen cijfers bevat, is het wss een straatzoekopdracht.
    // Toon dan alleen de unieke straten.
    if (!/\d/.test(query)) {
      return uniekeStraten;
    }

    // Anders, combineer straten en adressen, met straten eerst.
    return [...uniekeStraten, ...adresResults];
  } catch (e) {
    console.error('Fout bij zoeken adres:', e);
    return [];
  }
}

/**
 * Zoek adressen via de BAG API met gestructureerde parameters.
 * @param {object} params - Object met zoekparameters (bijv. { postcode: '1234AB', number: '10' })
 * @returns {Promise<Array<{ id: string, label: string }>>}
 */
export async function searchBagAdressenByParams(searchParams) {
  try {
    const path = 'adressen';
    const params = {
      ...searchParams,
      expand: 'openbareRuimte,woonplaats',
      pageSize: '20' // Beperk resultaten voor specifiekere zoekopdrachten
    };
    
    const { data, error } = await supabase.functions.invoke('bag-proxy', {
      body: { path, params }
    });

    if (error) {
        console.error(`Fout bij het zoeken naar adressen op params:`, error.message);
        return [];
    }

    if (!data?._embedded?.adressen) {
      return [];
    }

    // Deze functie retourneert alleen adresresultaten, niet "hele straat".
    return data._embedded.adressen.map(parseAdres).filter(Boolean);

  } catch (e) {
    console.error('Fout bij zoeken adres op params:', e);
    return [];
  }
}

/**
 * Haal adres op basis van BAG ID
 * @param {string} bagId - BAG ID van een adres
 * @returns {Promise<{ id: string, label: string }|null>}
 */
export async function getBagAdres(bagId) {
  if (!bagId) return null;

  try {
    // Gebruik de generieke fetcher, maar forceer 'adressen' endpoint
    const adresData = await fetchBagObject('adressen', bagId);
    if (!adresData) return null;

    // Gebruik de parser voor een consistente output
    return parseAdres(adresData);
  } catch (e) {
    console.error('Fout bij ophalen adres via BAG ID:', e);
    return null;
  }
}